import { useState } from "react";
import { Mail, CheckCircle2, Loader2 } from "lucide-react";

interface Props {
  variant?: "inline" | "card";
  heading?: string;
  subheading?: string;
}

export default function NewsletterSignup({
  variant = "card",
  heading = "Stay Cool with VertexAir",
  subheading = "Subscribe for seasonal HVAC tips, maintenance reminders and exclusive offers.",
}: Props) {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { setErr("Please enter a valid email."); return; }
    setBusy(true);
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/newsletter/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, firstName: firstName || null }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Sign up failed");
      setDone(true);
    } catch (e: any) {
      setErr(e?.message || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const wrapClass = variant === "card"
    ? "bg-gradient-to-br from-[#0F2F4F] to-[#1a4d7a] text-white rounded-2xl p-8 md:p-10 shadow-xl"
    : "bg-slate-50 border border-slate-200 rounded-xl p-6";

  return (
    <section className={wrapClass}>
      <div className="max-w-2xl mx-auto text-center">
        <div className={`inline-flex p-3 rounded-full mb-4 ${variant === "card" ? "bg-white/10" : "bg-primary/10"}`}>
          <Mail className={`w-6 h-6 ${variant === "card" ? "text-white" : "text-primary"}`} />
        </div>
        <h2 className={`text-2xl md:text-3xl font-bold mb-2 ${variant === "card" ? "text-white" : "text-slate-900"}`}>{heading}</h2>
        <p className={`text-sm md:text-base mb-6 ${variant === "card" ? "text-white/80" : "text-slate-600"}`}>{subheading}</p>

        {done ? (
          <div className={`flex items-center justify-center gap-2 px-5 py-4 rounded-lg font-medium ${variant === "card" ? "bg-white/10 text-white" : "bg-emerald-50 text-emerald-700"}`}>
            <CheckCircle2 className="w-5 h-5" /> Thanks! You're subscribed.
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2 max-w-xl mx-auto">
            <input
              type="text"
              placeholder="First name (optional)"
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
              className="flex-1 px-4 py-3 rounded-lg text-slate-900 bg-white border border-transparent focus:outline-none focus:border-white text-sm"
              autoComplete="given-name"
            />
            <input
              type="email"
              required
              placeholder="Your email address"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="flex-1 px-4 py-3 rounded-lg text-slate-900 bg-white border border-transparent focus:outline-none focus:border-white text-sm"
              autoComplete="email"
            />
            <button
              type="submit"
              disabled={busy}
              className={`px-6 py-3 rounded-lg font-semibold text-sm whitespace-nowrap disabled:opacity-60 flex items-center justify-center gap-2 ${variant === "card" ? "bg-[#1FB6B6] hover:bg-[#1aa3a3] text-white" : "bg-primary hover:bg-primary/90 text-white"}`}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {busy ? "Subscribing…" : "Subscribe"}
            </button>
          </form>
        )}

        {err && <p className="text-sm mt-3 text-amber-200">{err}</p>}
        <p className={`text-xs mt-4 ${variant === "card" ? "text-white/60" : "text-slate-500"}`}>We respect your privacy. Unsubscribe anytime.</p>
      </div>
    </section>
  );
}
